import React from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  CheckCircle2,
  Shield,
  Calendar,
  MapPin,
  Star,
  Droplet,
} from 'lucide-react';
import { Layout } from '../../components/layout/Layout';
import { SEO } from '../../components/shared/SEO';
import { SERVICES, COMPANY_INFO } from '../../data/mock';

export default function PlanoPage() {
  return (
    <Layout>
      <SEO
        title="Sprinkler Repair Plano TX | AAA Irrigation Service"
        description="TCEQ Licensed sprinkler repair in Plano, TX. Irrigation repair, backflow installation, head &amp; valve repair. Call (469) 751-3567 for fast service."
        keywords="sprinkler repair Plano TX, irrigation repair Plano, Plano sprinkler service, backflow preventer installation Plano TX, sprinkler head repair Plano, irrigation controller repair Plano TX"
        canonical="https://aaairrigationservice.com/locations/plano"
        extraSchema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Sprinkler Repair in Plano, TX",
          "serviceType": "Sprinkler & Irrigation Repair",
          "description": "TCEQ Licensed sprinkler and irrigation repair in Plano, TX — backflow installation, controller upgrades, valve and head repair.",
          "url": "https://aaairrigationservice.com/locations/plano",
          "provider": { "@id": "https://aaairrigationservice.com/#business" },
          "areaServed": {
            "@type": "City",
            "name": "Plano",
            "containedInPlace": { "@type": "State", "name": "Texas" }
          }
        }}
      />

      {/* Hero */}
      <section className="relative bg-[#0f172a] py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#1b7abf] rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#059669]/20 border border-[#059669]/40 rounded-full px-4 py-1.5 mb-6">
              <Shield className="w-4 h-4 text-[#059669]" />
              <span className="text-sm font-bold text-[#059669]">
                TCEQ License #{COMPANY_INFO.license}
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black italic uppercase text-white leading-none mb-6">
              Sprinkler Repair &amp;<br />
              <span className="text-[#1b7abf]">Irrigation Services</span><br />
              in Plano, TX
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              AAA Irrigation Service is the go-to TCEQ licensed sprinkler repair
              company in Plano. We serve every neighborhood from West Plano's Legacy
              corridor to East Plano's established communities with emergency
              service and fully stocked trucks.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:${COMPANY_INFO.phone}`}
                className="inline-flex items-center justify-center gap-3 bg-[#ea580c] hover:bg-[#c2410c] text-white font-black text-xl px-8 py-5 rounded-2xl shadow-2xl shadow-orange-600/40 hover:shadow-orange-600/60 transition-all duration-300 hover:scale-105 uppercase tracking-wide"
              >
                <Phone className="w-6 h-6" />
                {COMPANY_INFO.phoneFormatted}
              </a>
              <Link
                to="/schedule"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border-2 border-white/50 text-white font-bold text-lg px-8 py-5 rounded-2xl transition-all duration-300"
              >
                <Calendar className="w-5 h-5" />
                Schedule Online
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-[#1b7abf] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-6 text-white text-sm font-bold">
            {['TCEQ Licensed', 'Emergency Service', '1-Year Warranty', '4.9 Stars · 29 Reviews'].map((t) => (
              <span key={t} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-white/80" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Local content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-1.5 mb-6">
              <MapPin className="w-4 h-4 text-[#1b7abf]" />
              <span className="text-sm font-bold text-[#1b7abf] uppercase tracking-wide">
                Plano, TX
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black italic uppercase text-slate-900 mb-6">
              Why Plano Homeowners Choose <span className="text-[#1b7abf]">AAA Irrigation</span>
            </h2>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              Plano's mature neighborhoods feature irrigation systems ranging from
              brand-new smart controllers to 20-year-old setups that need
              professional attention. The city's mix of Blackland Prairie clay and
              Austin Chalk soil puts constant stress on underground pipes, making
              leak detection and pressure testing essential maintenance for every
              Plano homeowner.
            </p>
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              The City of Plano requires backflow preventers on all irrigation systems. AAA Irrigation installs backflow preventers and coordinates directly with a certified tester to get your system inspected and compliant — typically within 2 days. One call handles everything.
            </p>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              With North Texas summers routinely exceeding 100 degrees, your
              irrigation system is the difference between a healthy lawn and
              thousands of dollars in dead landscaping. Our trucks carry parts for
              every major brand, and we complete most repairs on the first visit.
              No second trips. No waiting for parts. That is the AAA difference.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-1.5 mb-4">
              <Droplet className="w-4 h-4 text-[#1b7abf]" />
              <span className="text-sm font-bold text-[#1b7abf] uppercase tracking-wide">
                Our Services
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black italic uppercase text-slate-900 mb-3">
              Irrigation Services in <span className="text-[#1b7abf]">Plano, TX</span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Complete irrigation repair and maintenance serving all Plano
              neighborhoods including Legacy, Willow Bend, and Preston Hollow.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((service) => (
              <Link
                key={service.id}
                to={`/services/${service.id}`}
                className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-[#1b7abf]/40 hover:shadow-lg transition-all duration-200"
              >
                <h3 className="font-black text-slate-900 group-hover:text-[#1b7abf] uppercase tracking-wide text-base mb-2 transition-colors">
                  {service.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {service.shortDescription}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="w-7 h-7 text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <h2 className="text-3xl font-black italic uppercase text-slate-900 mb-2">
            Trusted by Plano Homeowners
          </h2>
          <p className="text-slate-500 mb-8">4.9 Stars · 29 Reviews</p>
          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
            <p className="text-slate-700 text-lg leading-relaxed italic mb-4">
              &ldquo;Best irrigation company in the DFW area. TCEQ licensed and very
              knowledgeable. They found a leak that two other companies missed and
              fixed everything in one visit. Our water bill dropped immediately.&rdquo;
            </p>
            <p className="font-bold text-slate-900">Sarah L. — Plano, TX</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1b7abf] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-white rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-black italic uppercase text-white mb-4">
            Need Sprinkler Repair in Plano?
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Call now for emergency service or schedule your appointment online.
            TCEQ Licensed. 1-year warranty on all parts and labor.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={`tel:${COMPANY_INFO.phone}`}
              className="inline-flex items-center gap-3 bg-[#ea580c] hover:bg-[#c2410c] text-white font-black text-xl px-10 py-5 rounded-2xl shadow-2xl transition-all duration-300 hover:scale-105 uppercase tracking-wide w-full sm:w-auto justify-center"
            >
              <Phone className="w-6 h-6" />
              {COMPANY_INFO.phoneFormatted}
            </a>
            <Link
              to="/schedule"
              className="inline-flex items-center gap-2 bg-white text-[#1b7abf] hover:bg-blue-50 font-black text-lg px-10 py-5 rounded-2xl transition-all duration-300 uppercase tracking-wide w-full sm:w-auto justify-center"
            >
              <Calendar className="w-5 h-5" />
              Schedule Service
            </Link>
          </div>
          <p className="text-white text-sm mt-6">
            Powered by Boatman Systems&trade;
          </p>
        </div>
      </section>
    </Layout>
  );
}
